import {Injectable} from '@angular/core'
import {environment} from "environments/environment"
import {HttpClient} from "@angular/common/http"
import {AuthService} from "app/core/auth/auth.service"
import {BehaviorSubject, catchError, filter, map, of, take} from "rxjs"
import {UserService} from "app/core/user/user.service"
import {ExtProfilesService} from "./ext-profiles/ext-profiles.service"
import {Router} from "@angular/router"
import {PublicationMessagesService} from "./publication-messages.service"
import {EventMessagesService, eventType} from "@store/event-messages.service"
import {LangsListParamsService} from "@store/langs-list-params.service"
import {ToastrService} from 'ngx-toastr'
import Bugsnag from '@bugsnag/js'
import {UrlService} from "@store/url.service";
import {AppModeService} from '@store/services/app-mode.service'
import {cloneDeep} from "lodash-es";
import {FlowFeedbackService} from "@store/flow-feedback/flow-feedback.service";
import {AUDIO_SCREENOUT_MESSAGE} from "./ext-profiles/constants/audio-recorder-ext-profile";
import {TranslocoService} from "@ngneat/transloco";

export interface IPublicRedirectStatus {
  doRedirect: boolean;

  state?: string;
  url?: string;

}

export interface ICintTokens {
  complete: string;
  screenout: string;
}

export interface IPublication {
  pubUuid: string;
  generalInfo: {
    title: string;
    published: Date;
    surveysAlias: {
      doAliasSurvey: boolean;
      aliases: Record<string, Record<string, string>>
    },
    moreOptions: {
      saveFromFirebase: boolean;
      preventFromReturn: boolean;
    };
    hasAudioQuestion?: boolean;
    audioQuestionRules?: {
      questionKey: string;
      rules: {
        stopIf?: any;
        visibleIf?: any;
        hideIf?: any;
      };
    }[];
  }
  redirectInfos: {
    hasRedirectionLinks: boolean;
    complete?: string;
    screenout?: string;
  };

  surveysOptions: any;

  isScreenOut: boolean;

  cintTokens: ICintTokens;

  extProfilsNeeded?: {
    all: string[],
    ask: string[],
    extDef: any,
  }

}


const default_state: IPublication = {
  pubUuid: null,
  generalInfo: {
    title: null,
    published: null,
    surveysAlias: {
      doAliasSurvey: false,
      aliases: {},
    },
    moreOptions: {
      saveFromFirebase: true,
      preventFromReturn: false,
    }
  },
  redirectInfos: {
    hasRedirectionLinks: false,
  },
  surveysOptions: {},

  isScreenOut: false,

  cintTokens: null,

  extProfilsNeeded: {
    all: [],
    ask: [],
    extDef: {},
  }

}

export interface IAvailableSurveys {
  index: string[];
  survey: Record<string, IAvailableSurvey>
}

//
// A synchroniser avec --> src/app/models/Surveys/survey.ts:38 dans l'app Client
//
export interface ISurveyReward {
  hasReward: boolean;
  default: number;
}

export interface IAvailableSurvey {
  id: string;
  title: string;
  description: string;
  monetization: number;
  media: Record<string, string>
  reward: ISurveyReward
}

export interface IGroup {
  index: string[],
  data?: Record<string, {
      title: string,
      contentToDisplay: {
        surveys: string[],
        messages: string[],
      }
      // "contentToDisplay": {
      //     "surveys": [
      //         2610
      //     ],
      //     "messages": [
      //         "c7215eb1-a1bc-47a8-bcd9-c35a05ccd838"
      //     ]
      // }
    }>
}

@Injectable({
  providedIn: 'root'
})
export class PublicationAccessService {

  BASE_URL = environment.apiUrl
  pubUuid: string
  comId: number

  hasPendingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  debugInfos$: BehaviorSubject<any> = new BehaviorSubject<any>({})
  publication$: BehaviorSubject<IPublication> = new BehaviorSubject<IPublication>(default_state)
  publicRedirectStatus$: BehaviorSubject<IPublicRedirectStatus> = new BehaviorSubject<IPublicRedirectStatus>({
    doRedirect: false,
  })
  allGroups$: BehaviorSubject<IGroup> = new BehaviorSubject<IGroup>({index: []})
  alreadyAnswered$: BehaviorSubject<string[]> = new BehaviorSubject<[]>([])
  allAnswered$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  electedGroups$: BehaviorSubject<string[]> = new BehaviorSubject<[]>([])
  electedSurveys$: BehaviorSubject<IAvailableSurveys> = new BehaviorSubject<IAvailableSurveys>({
    index: [],
    survey: {}
  })
  allSurveyIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([])

  /**
   * Guard anti double-redirect.
   * Une fois qu'on a déclenché une redirection (window.location.replace),
   * on n'en autorise aucune autre pour ce cycle.
   */
  private hasIssuedRedirect = false

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private _httpClient: HttpClient,
    private _authService: AuthService,
    public userService: UserService,
    private extProfilesService: ExtProfilesService,
    private publicationMessagesService: PublicationMessagesService,
    private eventMessagesService: EventMessagesService,
    private langsListParamsService: LangsListParamsService,
    private urlService: UrlService,
    private appModeService: AppModeService,
    private flowFeedbackService: FlowFeedbackService,
    private translocoService: TranslocoService,
  ) {

    this.extProfilesService.collectStatus$.subscribe((status) => {

      if (status) {
        this.evaluatePublicationRules()
      }

    })

    this.allAnswered$.next(false)

  }

  //
  // Get publication informations
  //
  public doGetMetasInfos(pubUuid: string, comId: number) {
    this.hasIssuedRedirect = false
    this.publicRedirectStatus$.next({doRedirect: false})

    localStorage.setItem('lastPubuuid', JSON.stringify({
      pubUuid,
      comId,
    }))

    this.pubUuid = pubUuid
    this.comId = comId

    this.eventMessagesService.setPubUuid(pubUuid)

    this.flowFeedbackService.publicationIsLoading(true);
    this.hasPendingRequest$.next(true)

    const endpoint = `${this.BASE_URL}/appCommunity/getPublicationInfos`
    const body = {
      pubUuid,
      comId: comId,
      debug: this.appModeService.isDebug$.value,
    }
    return this._httpClient.post(endpoint, body).pipe(
      catchError(err => {

        console.error('[doGetMetasInfos] doGetMetasInfos error:', err);

        Bugsnag.notify(err, (event) => {
          event.addMetadata('Publication Access Service', {
            method: this.doGetMetasInfos.name,
            body,
            endpoint
          })
        })

        this.toastr.error(
          `An error occurred while trying to get publication informations (${pubUuid}).`,
          'Error - Publication informations.'
        );

        this.flowFeedbackService.publicationIsLoading(false);
        this.hasPendingRequest$.next(false);

        return of(false);

      }),
      map((response: any) => {

          const data = response.data

          if (!data) {
            this.hasPendingRequest$.next(false)
            return false
          }

          if (!data.infos?.generalInfo?.published && !this.appModeService.isDebug$.value) {

            this.toastr.warning('This publication is not active.')

            // this.router.navigate(['/error'], {
            //   queryParams: {
            //     pubUuid: this.pubUuid,
            //     reason: 'Publication not active.'
            //   }
            // }).then()

            this.flowFeedbackService.publicationIsLoading(false);
            this.hasPendingRequest$.next(false);

            return false;

          }

          //
          // Prise en charge de la limitation des langues
          //
          this.langsListParamsService.updateLangsList(response.data.infos?.generalInfo?.availablesLang || ['fr', 'en'])
          this.langsListParamsService.askLang();
          //
          //
          //

          //
          // Avant debugInfos$ : les abonnés (ex. panneau debug) lisent allSurveyIds pour lister les publications.
          //
          this.allSurveyIds$.next(data.allSurveysIds || [])

          // Du debug ?
          this.debugInfos$.next({
            publication: data.debugInfos,
          })

          //
          // Publication messages
          //
          this.publicationMessagesService.set(data.infos?.publicationMessages)

          //
          // Groupes
          //
          const lsc = {
            '*noGroup*': {
              pubUuid: this.pubUuid,
              title: '*noGroup*',
              contentToDisplay: {
                surveys: data.allSurveysIds,
                messages: [],
              }
            }
          }
          const gIndex: string[] = ['*noGroup*']
          const conditionGroups = data.infos?.surveyConditions?.groups ?? []
          conditionGroups.forEach((sgc) => {

            gIndex.push(sgc.id)

            lsc[sgc.id] = {
              title: sgc.groupTitle,
              contentToDisplay: sgc.contentToDisplay,
            }

          })

          this.allGroups$.next({
            index: gIndex,
            data: lsc,
          })

          //
          // Publication infos:
          //
          const prevPub = this.publication$.value
          const infos = {...data.infos}
          if (infos.redirectInfos == null) {
            infos.redirectInfos = prevPub.redirectInfos
          }
          this.publication$.next({
            ...prevPub,
            ...infos,
            extProfilsNeeded: {...data.extProfilsNeeded}
          })

          if (this.publication$.value.redirectInfos?.hasRedirectionLinks) {
            // Pour faire afficher un warning si jamais...
            if (this.publication$.value.redirectInfos.screenout) {
              this.urlService.buildRedirectUrl(this.publication$.value.redirectInfos.screenout);
            }
            if (this.publication$.value.redirectInfos.complete) {
              this.urlService.buildRedirectUrl(this.publication$.value.redirectInfos.complete);
            }
          }

          // Au passage on pupdate l'IP du user courant :)
          this.userService.updateIP(data.sourceIp)

          this.hasPendingRequest$.next(false)
          this.flowFeedbackService.publicationIsLoading(false);

          return response

        }
      )
    )

  }

  evaluatePublicationRules(isComplete = false, debug = false) {
    if (!this.pubUuid) {
      this.toastr.warning(`Il n'y a aucun uuid de publication. Aucune évaluation ne peut être effectuée.`, `Évaluation impossible`)
      return false
    }

    this.hasPendingRequest$.next(true)

    const endpoint = `${this.BASE_URL}/appCommunity/evaluateRules`
    const body = {
      pubUuid: this.pubUuid,
      comId: this.comId,
      extValues: this.userService.user.isPublic ? this.extProfilesService.getExtProfiles() : {},
      debug,
    }

    return this._httpClient.post(endpoint, body).subscribe({
      next: (response: any) => {
        this.hasPendingRequest$.next(false)

        const data = cloneDeep(response.data)

        if (!data) {
          return
        }

        this.debugInfos$.next({
          ...this.debugInfos$.value,
          evaluation: {
            evalResult: data.evalResult,
            surveysInfo: data.surveysInfo,
          },
        })

        const electedGroups: string[] = []
        let alreadyAnswered: string[] = []
        let finalElectedSurveys = {}

        data.evalResult?.surveyIds?.forEach((aSurvey) => {
          if (!electedGroups.find(g => g === aSurvey.groupId)) {
            electedGroups.push(aSurvey.groupId)
          }
        })

        this.electedGroups$.next(electedGroups)

        if (data.allreadyAnswered.length) {
          alreadyAnswered = data.allreadyAnswered.join().split(',')

          const allSurveys = Object.keys(data.surveysInfo)

          allSurveys.forEach((aSurveyId) => {
            if (!alreadyAnswered.includes(`${aSurveyId}`)) {
              finalElectedSurveys[aSurveyId] = data.surveysInfo[aSurveyId]
            }
          })
        } else {
          finalElectedSurveys = data.surveysInfo
        }

        this.alreadyAnswered$.next(alreadyAnswered)
        this.electedSurveys$.next({
          index: Object.keys(finalElectedSurveys),
          survey: finalElectedSurveys,
        })

        //
        // Détermine le screen-out ou pas.
        //
        if (this.extProfilesService.collectStatus$.value) { // <--- On a bien tous les ext pour pouvoir évaluer ;)

          this.publication$.next(
            {
              ...this.publication$.value,
              // ScreenOut ou pas...
              isScreenOut: this.electedSurveys$.value.index.length === 0,
            }
          )

          // On a plus de sondage...
          if (this.electedSurveys$.value.index.length === 0) {

            // Rien de déjà répondu = screenOut !
            if (alreadyAnswered.length === 0) {
              // ScreenOut !

              if (isComplete) {
                this.doComplete()
              } else {
                this.doScreenOut()
              }
            } else {

              // Au moins un sondage déjà répondu → complete / redirection (sans exiger 100 % des sondages de la publication).
              if (alreadyAnswered.length > 0) {
                this.doComplete()
              }

            }


          } else {

            this.eventMessagesService.emitEvent(eventType.landingReadyToReply, data.surveysInfo)
            this.doStartToReply(data.infos?.surveysOptions?.allwaysDisplayHomePageContributor)


          }

        }

        return response

      },
      error: err => {
        this.hasPendingRequest$.next(false)
        Bugsnag.notify(err, (event) => {
          event.addMetadata('Publication Access Service', {
            method: this.evaluatePublicationRules.name,
            body,
            endpoint
          })
        })
      }
    })

  }

  private doStartToReply(allwaysDisplayHomePageContributor) {

    this.allAnswered$.next(false)

    // ET si il n'y a pas de message de bienvenue !
    if (this.publicationMessagesService.messages$.value.indexBy?.['welcome']?.length > 0) {
      return true;
    }

    //
    // Afficher le captcha si besoin
    // Captcha désactivé - cette vérification est ignorée
    //
    // if (this.userService.user$.value?.isPublic && !this.userService.user$.value?.publicStatus?.captchaValidated) {
    //   return false;
    // }

    //
    // Si il y en a juste 1 ET pas en mode debug ou mode publication afficher home -> redirect
    //
    if (!this.appModeService.isDebug$.value && !allwaysDisplayHomePageContributor) {
      if (this.electedSurveys$.value.index.length === 1) {
        this.extProfilesService.setupRandom();
        this.navigateToSurvey(this.electedSurveys$.value.index[0]).then();
      }
    }

  }

  public doNormalScreenOut(): void {
    // Normal screenout without audio-specific message
    this.publication$.next({
      ...this.publication$.value,
      isScreenOut: true,
    });

    this.electedSurveys$.next({
      index: [],
      survey: {},
    });

    this.allAnswered$.next(false);

    this.doScreenOut();
  }

  public screenOutForAudioRequirement(): void {
    // Add the specific audio screenout message
    // Convert title to string based on current language
    const currentLang = this.translocoService.getActiveLang() || 'en';
    const messageWithStringTitle = {
      ...AUDIO_SCREENOUT_MESSAGE,
      title: typeof AUDIO_SCREENOUT_MESSAGE.title === 'string' 
        ? AUDIO_SCREENOUT_MESSAGE.title 
        : (AUDIO_SCREENOUT_MESSAGE.title[currentLang] || AUDIO_SCREENOUT_MESSAGE.title.en || 'ScreenOut'),
    };
    this.publicationMessagesService.addAudioScreenOutMessage(messageWithStringTitle);

    this.publication$.next({
      ...this.publication$.value,
      isScreenOut: true,
    });

    this.electedSurveys$.next({
      index: [],
      survey: {},
    });

    this.allAnswered$.next(false);

    this.doScreenOut();
  }

  private doScreenOut() {
    this.eventMessagesService.emitEvent(eventType.landingScreenOut, this.extProfilesService.getExtProfiles())

    this.extProfilesService.persistUserExtProfiles(`screenout_pubUuid_${this.pubUuid}`, new Date().toISOString()).subscribe({
      next: () => {
        this.doRedirectForPublicUser(eventType.landingScreenOut)
      },
      error: (err) => {
        Bugsnag.notify(err, (event) => {
          event.addMetadata('Publication Access Service', {
            method: this.doScreenOut.name,
            errMsg: err.message
          })
        })
        this.doRedirectForPublicUser(eventType.landingScreenOut)
      }
    })
  }

  private doComplete() {

    this.eventMessagesService.emitEvent(eventType.landingComplete, this.extProfilesService.getExtProfiles())
    this.allAnswered$.next(true)

    this.doRedirectForPublicUser(eventType.landingComplete)

  }

  doRedirectForPublicUser(state: eventType) {

    // Pas un user public, on a pas besoin de rien faire :)
    if (!this.userService.user$.value.isPublic) {
      return false;
    }

    let url = null;

    //
    // User Cint => même logique de redirection que les autres publics (URLs publication ou tokens Cint) ;
    // screenout conserve le callback Samplicio historique (RIS=20).
    //
    if (this.userService.user.publicStatus?.rid) {
      const rid = encodeURIComponent(String(this.userService.user.publicStatus.rid));
      if (state === eventType.landingScreenOut) {
        state = eventType.cintScreenoutRedirect;
        url = `https://samplicio.us/s/ClientCallBack.aspx?RIS=20&RID=${rid}`;
      } else if (state === eventType.landingComplete) {
        state = eventType.cintCompleteRedirect;
        const pub = this.publication$.value;
        if (pub.cintTokens?.complete) {
          url = this.urlService.buildRedirectUrl(pub.cintTokens.complete);
        } else if (pub.redirectInfos?.hasRedirectionLinks && pub.redirectInfos.complete) {
          url = this.urlService.buildRedirectUrl(pub.redirectInfos.complete);
        }
      }
    } else {
      if (this.publication$.value.redirectInfos?.hasRedirectionLinks) {

        if (state === eventType.landingScreenOut && this.publication$.value.redirectInfos.screenout) {
          state = eventType.publicScreenoutRedirect;
          url = this.urlService.buildRedirectUrl(this.publication$.value.redirectInfos.screenout);
        }
        if (state === eventType.landingComplete && this.publication$.value.redirectInfos.complete) {
          state = eventType.publicCompleteRedirect;
          url = this.urlService.buildRedirectUrl(this.publication$.value.redirectInfos.complete);
        }

      }
    }

    if (url) {
      // Eviter les redirections multiples si la méthode est appelée 2x
      // (audio + screenout, etc.)
      if (this.hasIssuedRedirect) {
        return
      }
      this.hasIssuedRedirect = true

      this.eventMessagesService.emitEvent(state, {
        url,
      })


      if (!this.appModeService.isDebug$.value) {

        this.eventMessagesService.hasPendingRequest$
          .pipe(
            filter((value) => !value),
            take(1),
          )
          .subscribe(() => {
            this.redirectIfNoPendingRequest(false, url)
          })

      }

      this.publicRedirectStatus$.next({
        doRedirect: true,
        state,
        url,
      })

    }

  }

  private redirectIfNoPendingRequest(value: boolean, url) {
    if (!value) {
      window.location.replace(url)
    }
  }

  public async navigateToSurvey(surveyId: string) {

    if (surveyId) {
      await this.router.navigate(['/survey', surveyId], {
        queryParams: {
          pubUuid: this.pubUuid,
          from: 'clickFromTile',
          jwtToken: this._authService.accessToken?.length === 0 ? null : this._authService.accessToken,
          debug: this.appModeService.isDebug$.value ? '123' : null,
          testSurvey: this.appModeService.isTestSurvey$.value ? '1' : null,
        }
      }).then(() => {
        //
      });

    }
  }
}

